import { cn } from '@seen/ui'
import { IconArrowLeft, IconChevronRight } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'

export interface CommandCrumb {
  id: string
  label: ReactNode
  // Where the crumb leads. The last crumb is the current level and has none.
  onSelect?: () => void
}

// The path into a nested command menu, above its search input. Its controls
// keep the pointer from taking focus so the input behind it stays live.
export function CommandBreadcrumb({
  crumbs,
  onBack,
  className,
}: {
  crumbs: CommandCrumb[]
  onBack: () => void
  className?: string
}): JSX.Element {
  return (
    <nav
      aria-label="Menu path"
      className={cn(
        'flex items-center gap-1 border-b px-2 py-1.5 text-muted-foreground text-xs',
        className
      )}
    >
      <button
        type="button"
        aria-label="Back"
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onBack}
        className="rounded-xs p-0.5 hover:bg-accent hover:text-accent-foreground"
      >
        <IconArrowLeft className="size-4" />
      </button>
      <ol className="flex items-center gap-1">
        {crumbs.map((crumb, index) => {
          const current = index === crumbs.length - 1
          return (
            <li key={crumb.id} className="flex items-center gap-1">
              {index > 0 && (
                <IconChevronRight className="size-3.5 shrink-0 opacity-60" />
              )}
              {current ? (
                <span
                  aria-current="page"
                  className="flex items-center gap-1 font-medium text-foreground"
                >
                  {crumb.label}
                </span>
              ) : (
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={crumb.onSelect}
                  className="flex items-center gap-1 rounded-xs px-1 hover:bg-accent hover:text-accent-foreground"
                >
                  {crumb.label}
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
